import { useEffect, useRef } from "react";

export const UNSAVED_MSG = "You have unsaved changes. Leave without saving?";

// Warns the user before they navigate away with unsaved edits.
// Covers: browser refresh/tab close (beforeunload), and in-app link clicks
// (sidebar/nav tabs). Returns a guard() the caller uses for programmatic
// navigations (Back button, prev/next): guard() === true means it's OK to leave.
export function useUnsavedChanges(dirty) {
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (!dirtyRef.current || e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      const target = a.getAttribute("target");
      if (target && target !== "_self") return; // opens a new tab — page isn't left
      if (a.hasAttribute("download")) return;
      // external links: the real unload is caught by beforeunload
      if (/^https?:\/\//i.test(href) && a.origin && a.origin !== window.location.origin) return;
      // clicking the current page/tab — nothing to lose
      if (a.pathname === window.location.pathname && a.search === window.location.search) return;
      if (!window.confirm(UNSAVED_MSG)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return () => !dirtyRef.current || window.confirm(UNSAVED_MSG);
}
